import React, { useContext, useState } from 'react';
import { Box, Modal } from '@mui/material';
import SuccessPanel from './SuccessPanel';
import FailurePanel from './FailurePanel';
import EmailPanel from './EmailPanel';
import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import { EmailModalProps } from './types';
import { checkEmail } from 'utils';
import { createEmailVerification } from 'services/emailVerificationService';
import { openNotification } from 'services/notificationService/notificationSlice';
import { useAppDispatch } from 'hooks';
import { EngagementViewContext } from './EngagementViewContext';
import ThankYouPanel from './ThankYouPanel';
import { EmailVerificationType } from 'models/emailVerification';
import { INTERNAL_EMAIL_DOMAIN } from 'constants/emailVerification';
import { EngagementVisibility } from 'constants/engagementVisibility';
import { analyticsService } from 'services/penguinAnalytics';

const EmailModal = ({ defaultPanel, open, handleClose }: EmailModalProps) => {
    const dispatch = useAppDispatch();
    const [formIndex, setFormIndex] = useState(defaultPanel);
    const [email, setEmail] = useState('');
    const { savedEngagement } = useContext(EngagementViewContext);
    const [isSaving, setSaving] = useState(false);

    const close = () => {
        handleClose();
        setFormIndex('email');
    };

    const updateTabValue = () => {
        if (!checkEmail(email)) {
            analyticsService.track({
                action: 'error',
                engagement_id: String(savedEngagement.id),
                engagement_name: savedEngagement.name,
                survey_id: String(savedEngagement.surveys[0]?.id || ''),
                text: 'Invalid email format',
            });
            setFormIndex('error');
        } else if (
            savedEngagement.visibility == EngagementVisibility.Internal &&
            !email.endsWith(INTERNAL_EMAIL_DOMAIN)
        ) {
            analyticsService.track({
                action: 'error',
                engagement_id: String(savedEngagement.id),
                engagement_name: savedEngagement.name,
                survey_id: String(savedEngagement.surveys[0]?.id || ''),
                text: 'Invalid domain for internal engagement',
            });
            setFormIndex('error');
        } else {
            handleSubmit();
        }
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            await createEmailVerification({
                email_address: email,
                survey_id: savedEngagement.surveys[0].id,
                type: EmailVerificationType.Survey,
            });
            try {
                window.snowplow('trackSelfDescribingEvent', {
                    schema: 'iglu:ca.bc.gov.met/verify-email/jsonschema/1-0-0',
                    data: { survey_id: savedEngagement.surveys[0].id, engagement_id: savedEngagement.id },
                });
            } catch (error) {
                console.log(error);
            }
            // Note: email_submitted event is now tracked server-side in met-api
            // with the verification_token for complete journey tracking
            dispatch(
                openNotification({
                    severity: 'success',
                    text: 'Email verification has been sent',
                }),
            );
            setFormIndex('success');
        } catch (error) {
            analyticsService.track({
                action: 'error',
                engagement_id: String(savedEngagement.id),
                engagement_name: savedEngagement.name,
                survey_id: String(savedEngagement.surveys[0]?.id || ''),
                text: 'Email verification failed',
            });
            dispatch(
                openNotification({
                    severity: 'error',
                    text: 'Error occurred while sending the email verification',
                }),
            );
            setFormIndex('error');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box>
                <TabContext value={formIndex}>
                    <TabPanel value="email">
                        <EmailPanel
                            email={email}
                            checkEmail={updateTabValue}
                            handleClose={() => close()}
                            updateEmail={setEmail}
                            isSaving={isSaving}
                            visibility={savedEngagement.visibility}
                        />
                    </TabPanel>
                    <TabPanel value="success">
                        <SuccessPanel handleClose={() => close()} email={email} />
                    </TabPanel>
                    <TabPanel value="thank you">
                        <ThankYouPanel handleClose={() => close()} />
                    </TabPanel>
                    <TabPanel value="error">
                        <FailurePanel
                            tryAgain={() => setFormIndex('email')}
                            handleClose={() => close()}
                            email={email}
                            visibility={savedEngagement.visibility}
                        />
                    </TabPanel>
                </TabContext>
            </Box>
        </Modal>
    );
};

export default EmailModal;
